import React, { Suspense, lazy } from 'react';
import { Route } from 'react-router-dom';
import RouteError from '@src/layout-components/route/route-error';
import prefetchAsyncComponent from '@src/layout-components/route/route-enhance/load-async-component';
import withEnhanceRouter from '@src/layout-components/route/with-enhance-router';
import anyhow from 'promise-anyhow';
import { delay } from '@xfe-team/utils/src/common-util';
import isWindowOnload from '@xfe-team/utils/src/is-window-onload';
import protect from '@src/error/protect';

/**
 * 准备组件 props 名称
 * @type {string}
 */
const PREPARE_COMPONENT_PROPS_NAME = 'getInitialProps';
/**
 * 路由增强
 */
class RouteEnhanceImpl extends React.PureComponent {
  // noinspection JSUnresolvedVariable,ES6ModulesDependencies
  static defaultProps = {
    /**
     * 是否使用 loading, 默认开启. loading 目前为小鸡 gif
     */
    useLoading: true,
    /**
     * 最小动画 loading 时间
     * 这个命名有点困惑, 主要意思是当动画出现的话, 那这个动画必须至少展示 800 毫秒,
     * 这是避免动画只展示 100 或过短时间让视觉有闪烁感
     */
    routeLoadingExpectMinShowTime: 600,
    /**
     * 路由动画延迟展示时间, 即当网速非常好的情况下
     * 如, 小于 100 毫秒时, 无需展示 loading
     * 这个有效的让用户有秒开感
     */
    routeLoadingDelayShowTime: 100,
    /**
     * 等待直到 window onload 后进入页面
     */
    waitTillWindowOnload: true
  };

  static getInitialState(overrideStates = {}) {
    return {
      isReady: false,
      isError: false,
      hasError: false,
      ComponentClass: null,
      componentProps: {},
      ...overrideStates
    };
  }

  state = RouteEnhanceImpl.getInitialState();

  /**
   * 获取组件的 initial props 方法, 类似 angular 路由的 resolve
   * @param ComponentClass
   * @param props
   * @returns {Promise<{}|*|{}>}
   */
  static async getComponentInitialProps(ComponentClass, props) {
    if (ComponentClass) {
      const prepareComponentProps = ComponentClass[PREPARE_COMPONENT_PROPS_NAME];
      if (typeof prepareComponentProps === 'function') {
        //TODO: 这里应该参考一下 next.js 预加载参数
        return prepareComponentProps({ ...props, prefetch: true }) || {};
      }
    }
    return {};
  }

  @protect()
  componentDidMount() {
    return this.bootstrap();
  }

  /**
   * 主逻辑, 启动当前组件流程
   */
  async bootstrap() {
    try {
      const { asyncRender, routeLoadingExpectMinShowTime, routeLoadingDelayShowTime, waitTillWindowOnload } = this.props;
      const { hasError } = this.state;
      const prefetchPromise = RouteEnhance.prefetch({ asyncRender, props: this.props });
      const { consumeTime, error } = await RouteEnhanceImpl.getAsyncConsumeTime(() =>
        anyhow([
          prefetchPromise,
          waitTillWindowOnload && isWindowOnload(),
          hasError ? delay(1000) : Promise.resolve() /* 如果曾经发生过错误, 为了优化用户体验, 延长最小加载时间 */
        ])
      );

      if (error) {
        // noinspection ExceptionCaughtLocallyJS
        throw error;
      }

      /**
       * 如果总等待时间大于"路由动画最小等待时间",
       * 那么我们让动画展示久一点吧
       */
      if (consumeTime > routeLoadingDelayShowTime) {
        const routeLoadingRequireShowTime = routeLoadingExpectMinShowTime - consumeTime;
        routeLoadingRequireShowTime > 0 && (await delay(routeLoadingRequireShowTime));
      }

      // 可能能够优化页面动画性能
      // await requestIdlePromise();
      this.updateState({ isReady: true, hasError: false, ...(await prefetchPromise) });
    } catch (e) {
      console.error(e);
      this.updateState({ isError: true });
    }
  }

  /**
   * 获取异步计算耗时时间
   * @param asyncAction - 异步操作函数
   *
   * e.g.
   * getAsyncConsumeTime(async ()=> await )
   *
   * @returns {Promise<{result: *, consumeTime: number, error: Error}>}
   */
  static async getAsyncConsumeTime(asyncAction) {
    const startTime = +new Date();
    const promise = await asyncAction();
    const consumeTime = +new Date() - startTime;
    let error;
    let result;
    try {
      result = await promise;
    } catch (e) {
      error = e;
    }
    return { result, consumeTime, error };
  }

  renderChildren() {
    const { isReady, ComponentClass, componentProps } = this.state;
    const { animate, history, routeLoadingDelayShowTime, useLoading } = this.props;
    if (isReady) {
      return <ComponentClass {...componentProps} />;
    } else {
      return <div />;
    }
  }

  updateState(nextState) {
    this.isUnmount || this.setState(nextState);
  }

  componentWillUnmount() {
    this.isUnmount = true;
  }

  /**
   * 异常重试
   * 当"路由错误"组件点击时, 重置当前组件状态并重启流程, 即调用 bootstrap 方法
   */
  onRouteErrorClick = () => {
    this.updateState(RouteEnhanceImpl.getInitialState({ hasError: true }), this.bootstrap);
  };

  render() {
    const { isError } = this.state;
    if (isError) {
      return <RouteError onClick={this.onRouteErrorClick} />;
    }
    return this.renderChildren();
  }
}

const RouteEnhanceImplInjected = withEnhanceRouter(RouteEnhanceImpl);

/**
 * 获取默认值
 * @param obj
 * @returns {*}
 */
const defaults = obj => (obj.default ? obj.default : obj);

/**
 * @param {Function} [asyncRender] - 异步渲染
 * @param {boolean} [native] - 原始的, 当开启时回归原始没有 loading, 没有 prefetch, 没有动画直接渲染组件
 * @param [*] restProps
 * @returns {*}
 */
const RouteEnhance = ({
  asyncRender,
  native,
  animate,
  useLoading,
  routeLoadingExpectMinShowTime,
  routeLoadingDelayShowTime,
  waitTillWindowOnload,
  ...restProps
}) => {
  if (native) {
    const LazyComponent = lazy(asyncRender);
    return (
      <Suspense fallback={null}>
        <LazyComponent />
      </Suspense>
    );
  }
  return (
    <Route
      {...restProps}
      render={() =>
        React.createElement(RouteEnhanceImplInjected, {
          asyncRender,
          native,
          animate,
          useLoading,
          routeLoadingExpectMinShowTime,
          routeLoadingDelayShowTime,
          waitTillWindowOnload
        })
      }
    />
  );
};

RouteEnhance.prefetch = async ({ asyncRender, prefetchUntilLoadAsyncComponent, props = {} }) => {
  const Component = await prefetchAsyncComponent(asyncRender);
  /**
   * 如果标识参数表明只需要加载 asyncComponent 为止, 则直接结束 prefetch
   */
  if (prefetchUntilLoadAsyncComponent) {
    return { prefetchUntilLoadAsyncComponent };
  }
  /**
   * 优先选择模块中的 default 暴露对象
   */
  const ComponentClass = defaults(Component);
  const componentProps = await RouteEnhanceImpl.getComponentInitialProps(ComponentClass, props);
  return { ComponentClass, componentProps };
};

export default RouteEnhance;
